/**
 * Chart Theme Module — Centralized Recharts brand theme for ClaimGenius
 *
 * Phase 2.4-recharts-theme: All chart styling centralized here.
 * Rules:
 * - Primary orange (#FF6600) is the ONLY signal color
 * - Grayscale for series differentiation
 * - Green (#22C55E) only for "done" / positive states
 * - Muted gray (#6B7280) for negative values (parens, never red)
 * - DM Mono for axis labels (financial data)
 * - Outfit for tooltip/legend prose
 * - DM Serif NOT used in charts
 */

import { CSSProperties, SVGProps } from 'react';

// ========================================================================
// COLOR PALETTE
// ========================================================================

/**
 * Ordered array of brand-safe series colors.
 * Primary orange first, then grayscale steps.
 * NO gold/teal/blue/purple/red (except done green).
 */
export const CHART_PALETTE: string[] = [
  '#FF6600', // Primary brand orange (index 0)
  '#A3A3A3', // Gray 400
  '#737373', // Gray 500
  '#525252', // Gray 600
  '#D4D4D4', // Gray 300
  '#404040', // Gray 700
];

/** Done/positive color - only for genuinely "done" states */
export const CHART_DONE_COLOR = '#22C55E';

/** Muted gray for negative money in parens - never red */
export const CHART_NEGATIVE_COLOR = '#6B7280';

/** Background/surface color for chart areas */
export const CHART_SURFACE_COLOR = '#121212';

/** Grid line color - subtle elevated surface */
export const CHART_GRID_COLOR = 'var(--color-border)';

/** Base text color for labels */
export const CHART_TEXT_COLOR = 'var(--color-text-secondary)';

// ========================================================================
// STYLE OBJECTS
// ========================================================================

/**
 * Axis tick style - DM Mono for financial data readability.
 * This overrides Recharts' default 'Inter' font.
 */
export const CHART_AXIS_STYLE: SVGProps<SVGTextElement> = {
  fontFamily: 'DM Mono, monospace',
  fontSize: 12,
  fill: 'var(--color-text-secondary)',
};

/**
 * Cartesian grid style - subtle dashed lines.
 */
export const CHART_GRID_STYLE: {
  stroke: string;
  strokeDasharray: string;
} = {
  stroke: 'var(--color-border)',
  strokeDasharray: '3 3',
};

/**
 * Tooltip content style - Outfit font for prose.
 */
export const CHART_TOOLTIP_STYLE: CSSProperties = {
  backgroundColor: 'var(--color-bg-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  fontFamily: 'Outfit, sans-serif',
  color: 'var(--color-text-primary)',
  fontSize: '12px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
};

/**
 * Tooltip label style (the header showing the x-axis value).
 */
export const CHART_TOOLTIP_LABEL_STYLE: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontWeight: 600,
  fontFamily: 'Outfit, sans-serif',
};

/**
 * Legend wrapper style - Outfit font for consistency.
 */
export const CHART_LEGEND_STYLE: CSSProperties = {
  fontFamily: 'Outfit, sans-serif',
  color: 'var(--color-text-secondary)',
  fontSize: '12px',
};

/**
 * Label list style for data labels on bars/lines.
 */
export const CHART_LABEL_STYLE: CSSProperties = {
  fontFamily: 'DM Mono, monospace',
  fontSize: 11,
  fill: 'var(--color-text-secondary)',
};

// ========================================================================
// HELPER FUNCTIONS
// ========================================================================

/**
 * Get a series color by index, cycling through the palette.
 * @param index - The series index (0-based)
 * @returns A color hex string from CHART_PALETTE
 */
export function getSeriesColor(index: number): string {
  return CHART_PALETTE[index % CHART_PALETTE.length];
}

/**
 * Get multiple series colors for multi-line/bar charts.
 * @param count - Number of colors needed
 * @returns Array of color hex strings
 */
export function getSeriesColors(count: number): string[] {
  return Array.from({ length: count }, (_, i) => getSeriesColor(i));
}

/**
 * Get a status color based on status type.
 * Only uses approved brand colors.
 */
export function getStatusColor(status: string): string {
  const statusMap: Record<string, string> = {
    completed: CHART_DONE_COLOR,
    done: CHART_DONE_COLOR,
    success: CHART_DONE_COLOR,
    active: CHART_PALETTE[0], // Brand orange
    pending: CHART_PALETTE[1], // Gray 400
    draft: CHART_PALETTE[2], // Gray 500
    failed: CHART_NEGATIVE_COLOR, // Muted gray (never red)
    unknown: CHART_PALETTE[3], // Gray 600
  };
  return statusMap[status.toLowerCase()] || CHART_PALETTE[2];
}

/**
 * Check if a color is in the forbidden list.
 * Useful for validation/testing.
 */
export function isForbiddenColor(color: string): boolean {
  const forbiddenColors = [
    '#F59E0B', // Gold/amber
    '#F59e0b',
    '#f59e0b',
    '#14B8A6', // Teal
    '#14b8a6',
    '#3B82F6', // Blue
    '#3b82f6',
    '#8B5CF6', // Purple
    '#8b5cf6',
    '#EF4444', // Red
    '#ef4444',
    '#DC2626', // Red (darker)
    '#dc2626',
  ];
  return forbiddenColors.includes(color);
}

// ========================================================================
// LEGACY COMPATIBILITY EXPORTS
// ========================================================================

/**
 * Deprecated: Use CHART_PALETTE instead.
 * Kept for backward compatibility during migration.
 */
export const LEGACY_CHART_COLORS = CHART_PALETTE;

/**
 * Default export for convenience imports.
 */
export default {
  CHART_PALETTE,
  CHART_DONE_COLOR,
  CHART_NEGATIVE_COLOR,
  CHART_AXIS_STYLE,
  CHART_GRID_STYLE,
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_LEGEND_STYLE,
  CHART_LABEL_STYLE,
  getSeriesColor,
  getSeriesColors,
  getStatusColor,
  isForbiddenColor,
};
